'use client';
import React, { useState } from 'react';
import { Mail, X, CheckCircle, AlertCircle, Loader2, Shield, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface EmailSignUpModalProps {
  onClose: () => void;
  initialMode?: 'signup' | 'signin' | 'account';
}

export default function EmailSignUpModal({ onClose, initialMode = 'signup' }: EmailSignUpModalProps) {
  const { signUp, signIn, deleteAccount, user } = useAuth();
  const [mode, setMode] = useState<'signup' | 'signin' | 'account'>(
    initialMode === 'account' ? 'account' : (user ? 'account' : initialMode)
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setStatus('loading');
    setMessage('');

    try {
      if (mode === 'signup') {
        await signUp(email, password);
        // Send confirmation email via edge function
        try {
          await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-mps-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, type: 'confirmation' }),
          });
        } catch {}
        setStatus('success');
        setMessage('Bestätigungs-E-Mail gesendet! Bitte prüfe dein Postfach und bestätige deine Adresse.');
      } else {
        await signIn(email, password);
        setStatus('success');
        setMessage('Erfolgreich angemeldet! Willkommen zurück, treuer Recke!');
        setTimeout(() => onClose(), 1500);
      }
    } catch (err: any) {
      setStatus('error');
      const msg = err?.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        setMessage('Diese E-Mail ist bereits registriert. Bitte melde dich an.');
        setMode('signin');
      } else if (msg.includes('Invalid login credentials')) {
        setMessage('Ungültige Anmeldedaten. Bitte prüfe E-Mail und Passwort.');
      } else if (msg.includes('Email not confirmed')) {
        setMessage('Bitte bestätige zuerst deine E-Mail-Adresse.');
      } else {
        setMessage(msg || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Bist du sicher? Dein Konto und alle Favoriten werden unwiderruflich gelöscht.')) return;
    setStatus('loading');
    setMessage('');
    try {
      await deleteAccount();
      setStatus('success');
      setMessage('Dein Konto wurde erfolgreich gelöscht. Auf Wiedersehen, treuer Recke!');
      setTimeout(() => onClose(), 2500);
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.message || 'Fehler beim Löschen des Kontos. Bitte versuche es erneut.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-primary" />
            <h2 className="font-display text-lg font-bold text-primary">
              {mode === 'account' ? 'Mein Konto' : mode === 'signup' ? 'Mein Phantasie Spectaculum — Familie beitreten' : 'Anmelden'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Disclaimer */}
          {mode !== 'account' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-800 leading-relaxed italic">
                ⚔️ Für die wahre MPS-Familie! Deine Favoriten werden gespeichert und sind auf allen Geräten verfügbar.
              </p>
            </div>
          )}

          {status === 'success' ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle size={48} className="text-green-600" />
              <p className="font-semibold text-foreground">{message}</p>
              {mode === 'signup' && (
                <p className="text-sm text-muted-foreground">
                  Nach der Bestätigung kannst du dich anmelden.
                </p>
              )}
              <button
                onClick={onClose}
                className="mt-2 btn-primary px-6 py-2 rounded-lg text-sm font-medium"
              >
                Schließen
              </button>
            </div>
          ) : mode === 'account' ? (
            <div className="space-y-4">
              <div className="bg-muted rounded-xl px-4 py-3">
                <p className="text-xs text-muted-foreground mb-1">Angemeldet als</p>
                <p className="text-sm font-medium text-foreground">{user?.email}</p>
              </div>

              {status === 'error' && message && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                  {message}
                </div>
              )}

              <div className="border-t border-border pt-4">
                <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">Gefahrenzone</p>
                <button
                  onClick={handleDeleteAccount}
                  disabled={status === 'loading'}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {status === 'loading' ? (
                    <><Loader2 size={15} className="animate-spin" /> Wird gelöscht…</>
                  ) : (
                    <><Trash2 size={15} /> Konto unwiderruflich löschen</>
                  )}
                </button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Alle Favoriten und Daten werden gelöscht.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Passwort
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  required
                  minLength={6}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              {status === 'error' && message && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full btn-primary py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <><Loader2 size={16} className="animate-spin" /> Bitte warten…</>
                ) : mode === 'signup' ? (
                  <><Mail size={16} /> Registrieren & Bestätigungs-E-Mail senden</>
                ) : (
                  'Anmelden'
                )}
              </button>

              <div className="text-center">
                {mode === 'signup' ? (
                  <p className="text-sm text-muted-foreground">
                    Bereits registriert?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('signin'); setMessage(''); setStatus('idle'); }}
                      className="text-primary hover:underline font-medium"
                    >
                      Anmelden
                    </button>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Noch kein Konto?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('signup'); setMessage(''); setStatus('idle'); }}
                      className="text-primary hover:underline font-medium"
                    >
                      Registrieren
                    </button>
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
